export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** The symbol, for the currencies this product actually sees. Falls back to
 *  the code, which is wrong for nobody and ugly for few. The trailing space is
 *  what keeps `NZD 2,400` from reading as one token. */
export function currencySymbol(currency: string): string {
  const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$' };
  return symbols[currency] ?? `${currency} `;
}

/**
 * A quote figure, from either shape the API serves it in: the inquiry detail
 * endpoint sends numbers, the three list routes send Postgres `numeric` as a
 * string. Coercing here rather than at each call site is deliberate — the high
 * end is absent far more often than not, and `Number(null)` is `0`, so every
 * call site that coerced for itself would be one more chance to render a single
 * figure as a range ending at zero.
 *
 * Absent is spelt both ways on the wire: the list routes send `null`, and the
 * detail page's quote object is optional, so a caller reaching through it with
 * `inquiry.quote?.amountHigh` gets `undefined`. Both mean one figure, so both
 * are taken — the alternative is a `?? null` at every such call site.
 */
type Figure = number | string;
type HighFigure = Figure | null | undefined;

const digits = (figure: Figure) => {
  const amount = Number(figure);
  return new Intl.NumberFormat('en-US', {
    // Whole dollars unless the vendor priced in cents, matching the
    // line-item convention in QuoteBreakdown. Judged per endpoint: a range
    // from a round low to an odd high should show each as it was quoted.
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/** En dash between ordinary spaces — the typographic range separator. A hyphen
 *  reads as a minus sign against digits. */
const RANGE = ' – ';

/**
 * The headline quote for a list row: one figure, or a range when the winning
 * quote priced several packages.
 *
 * The symbol goes on *both* ends of a range. A row carries no currency label
 * anywhere else, and a bare second number reads as a quantity rather than a
 * price.
 */
export function formatQuote({
  amount,
  amountHigh,
  currency,
}: {
  amount: Figure;
  amountHigh: HighFigure;
  currency: string;
}): string {
  const symbol = currencySymbol(currency);
  const low = `${symbol}${digits(amount)}`;
  return amountHigh == null ? low : `${low}${RANGE}${symbol}${digits(amountHigh)}`;
}

/** The same figure for QuoteField's input, which sits beside its own currency
 *  span and would otherwise show the symbol twice. */
export function formatQuoteDigits(amount: Figure, amountHigh: HighFigure): string {
  return amountHigh == null ? digits(amount) : `${digits(amount)}${RANGE}${digits(amountHigh)}`;
}
